import { TContent, TContentCreator } from '@/types/Content.types';

// Creator attribution now ships on every feed card (ContentDto.creator), so the grid can
// show real authorship without a per-card profile fetch. Every field is nullable — the
// block is populated for all content but individual parts can be missing — so each helper
// degrades to something sensible rather than rendering "null" or collapsing the layout.

export const PLATFORM_AUTHOR_NAME = 'MyOmnia';

// `fallbackName` lets a surface that has already fetched the fuller author profile use
// that name when the card payload has no creatorDisplayName — without making the profile
// fetch a prerequisite for rendering anything.
export const getCreatorName = (
  creator: TContentCreator | null | undefined,
  isPlatformContent: boolean,
  fallbackName?: string | null,
): string => {
  if (isPlatformContent) return PLATFORM_AUTHOR_NAME;

  return creator?.creatorDisplayName?.trim() || fallbackName?.trim() || 'Creator';
};

// `roles` is an ordered list of display labels, highest precedence first — not a single
// role. Compact surfaces show the first; roomier ones can show more.
export const getPrimaryRole = (creator: TContentCreator | null | undefined): string | null =>
  creator?.roles?.[0] ?? null;

export const getCreatorOrganization = (creator: TContentCreator | null | undefined): string | null =>
  creator?.organizationName ?? null;

// One line of attribution for tight spaces: "Maria Lopez · Peer Support · NYC Fire Dept".
// Platform content collapses to just "MyOmnia" (its org name is MyOmnia too, so repeating
// it would read as "MyOmnia · MyOmnia").
export const getCreatorLine = (content: Pick<TContent, 'creator' | 'isPlatformContent'>): string => {
  const { creator, isPlatformContent } = content;
  if (isPlatformContent) return PLATFORM_AUTHOR_NAME;

  return [getCreatorName(creator, false), getPrimaryRole(creator), getCreatorOrganization(creator)]
    .filter(Boolean)
    .join(' · ');
};

// A creator profile is only reachable for non-platform content with a known author id.
export const getCreatorProfileId = (content: Pick<TContent, 'creator' | 'isPlatformContent' | 'authorUserId'>): string | null => {
  if (content.isPlatformContent) return null;

  return content.creator?.id ?? content.authorUserId ?? null;
};
